import { useIsFeatureEnabled } from '@shared/core/featureFlags';

type UseIsDigitalPostcardEnabledArgs = Readonly<{
  /**
   * Whether this surface could offer Premium Digital at postcard size at all. Only used to skip
   * the flag read, so Amplitude records no exposure for a user who could never have seen it.
   */
  hasPostcardDigital: boolean;
}>;

type UseIsDigitalPostcardEnabledResult = Readonly<{
  isEnabled: boolean;
  /** True while the flag is in flight. A caller that seeds state from a filtered product list must wait on it. */
  loading: boolean;
}>;

/**
 * Premium Digital on 4x6/6x4 postcards, a product card_service is adding. Once it links the
 * `digitalCard` product to postcard templates, so eligibility is the product's presence and the
 * `printDigitalPostcards` flag is a kill switch over it: off means hide the postcard's own Premium
 * Digital, so the PDP falls back to borrowing it from the 5x7 sibling as it did before.
 */
export const useIsDigitalPostcardEnabled = ({ hasPostcardDigital }: UseIsDigitalPostcardEnabledArgs): UseIsDigitalPostcardEnabledResult => {
  // `requireFreshFetch`: turning the switch off must reach sessions already running, so a client
  // whose own flag fetch failed reads off rather than a cached `on` (featureFlags README, JW-136).
  const [flagEnabled, flagLoading] = useIsFeatureEnabled('printDigitalPostcards', { skip: !hasPostcardDigital, requireFreshFetch: true });
  return {
    isEnabled: hasPostcardDigital && flagEnabled,
    loading: hasPostcardDigital && flagLoading,
  };
};
